/**
 * WARNING:
 * This component MAY receive an optional caseId.
 * Page-level components MUST NOT copy this pattern.
 * All pages MUST call requireCaseId().
 */
import { NavLink, useParams } from 'react-router-dom';
// Removed useUiMode import (unused after removing "Defined Terms" toggle)
// import { useUiMode } from '@/contexts/UiModeContext';
import type { SubNavConfig } from '@/config/subNavConfig';
import { getCaseWideTabs } from '@/config/subNavConfig';

interface SecondarySubNavProps {
  config: SubNavConfig | null;
}

export default function SecondarySubNav({ config }: SecondarySubNavProps) {
  // Removed uiMode/setUiMode since "Defined Terms" toggle was removed
  // const { uiMode, setUiMode } = useUiMode();
  const { caseId } = useParams<{ caseId?: string }>();

  if (!config) {
    return null;
  }

  const hasContent = config.links.length > 0 || config.actionButton || config.showUiToggle;
  const caseWideTabs = config.showCaseTabs && caseId ? getCaseWideTabs(caseId) : null;

  // T-27: On case pages (showCaseTabs), CaseLayout owns the <h1> via CasePageHeader.
  // Only render the standalone title bar on non-case pages.
  if (!hasContent) {
    return (
      <div data-testid="secondary-subnav" className="border-b border-slate-700/50 bg-slate-800/60 px-6 py-4">
        {!caseWideTabs && (
          <h1 className="text-xl font-semibold text-slate-50">{config.title}</h1>
        )}
      </div>
    );
  }

  return (
    <div data-testid="secondary-subnav" className="border-b border-slate-700/50 bg-slate-800/60">
      {/* Case-Wide Tabs (if enabled) */}
      {caseWideTabs && (
        <div className="border-b border-slate-700/30 bg-slate-800/80">
          <nav className="flex gap-1 px-6" aria-label="Case navigation">
            {caseWideTabs.map((tab) => (
              <NavLink
                key={tab.path}
                to={tab.path}
                end={tab.path === `/cases/${caseId}`}
                className={({ isActive }) =>
                  `border-b-2 px-4 py-3 text-sm font-medium transition ${
                    isActive
                      ? 'border-brand-orange text-brand-orange'
                      : 'border-transparent text-slate-400 hover:border-slate-600 hover:text-slate-300'
                  }`
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}

      {/* Page-Specific Section — T-27: suppress title when CaseLayout provides CasePageHeader */}
      {(config.links.length > 0 || config.actionButton) && (
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-6">
          {/* Only show title in SecondarySubNav when NOT on a case page (no case-wide tabs).
              On case pages, CaseLayout renders CasePageHeader which owns the <h1> title. */}
          {!caseWideTabs && (
            <h1 className="text-xl font-semibold text-slate-50">{config.title}</h1>
          )}
          
          {config.links.length > 0 && (
            <nav className="flex gap-1" aria-label="Case phase actions">
              {config.links.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  end
                  className={({ isActive }) =>
                    `rounded-md px-3 py-1.5 text-sm font-medium transition ${
                      isActive
                        ? 'bg-slate-700 text-slate-50'
                        : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-50'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
          )}
        </div>

        <div className="flex items-center gap-3">
          {config.actionButton && (
            <NavLink
              to={config.actionButton.path}
              className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2"
            >
              {config.actionButton.label}
            </NavLink>
          )}
        </div>
      </div>
      )}
    </div>
  );
}